import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { dequal } from 'dequal'

import { usePrevious } from '@sequencehq/utils'

import { haveResolveInputsChanged } from './utils/haveResolveInputsChanged'
import { resolveDependencies } from './utils/resolveDependencies'
import { selectStaleDependencies } from './utils/selectStaleDependencies'
import type {
  AutomationAnalysis,
  DependencyResolutionRunOutcome
} from './simulation.types'

/**
 * Everything one round of fetching needs. These are handed to
 * `startDependencyResolutionRun` rather than closed over, so it keeps the same
 * identity for the life of the hook and the effect below is driven by the
 * inputs changing rather than by the function being rebuilt.
 */
type ResolveInputs = {
  analysis: AutomationAnalysis
  workflowInput: Record<string, unknown>
  useCacheValueFor: string[]
  workflowInputKey: string
  useCacheValueForKey: string
}

/**
 * A finished round of fetching, tagged with the inputs it was fetched for. We
 * keep the inputs alongside it so we can tell whether it still describes the
 * quote being looked at, or belongs to a previous one.
 */
type ResolvedRun = Pick<
  ResolveInputs,
  'analysis' | 'workflowInputKey' | 'useCacheValueForKey'
> & { value: DependencyResolutionRunOutcome }

/**
 * Fetches everything the conditions read. This is the one place an effect is
 * the right tool, because it's going out to the network rather than working
 * something out from what we already have.
 */
export const useDependencyResolution = ({
  analysis,
  isEnabled,
  useCacheValueFor: useCacheValueForProp,
  workflowInput,
  workflowInputKey
}: {
  analysis: AutomationAnalysis | undefined
  isEnabled: boolean
  useCacheValueFor: string[] | undefined
  /** Already merged with the per-trigger defaults by the caller. */
  workflowInput: Record<string, unknown>
  /** The caller's own inputs as text, which is what we compare rounds on. */
  workflowInputKey: string
}) => {
  // The caller passes this inline, so we compare the contents rather than the
  // object. Keying off the object would throw away the answer, and start the
  // fetching again, on every single render.
  const useCacheValueForKey = JSON.stringify(useCacheValueForProp ?? [])

  const useCacheValueFor = useMemo<string[]>(
    () => JSON.parse(useCacheValueForKey),
    [useCacheValueForKey]
  )

  const [resolved, setResolved] = useState<ResolvedRun | undefined>(undefined)
  const [isRunning, setIsRunning] = useState(false)
  // Counts the rounds of fetching we've started. A round that finishes after a
  // newer one began has been overtaken, and writing its answer down would put
  // the previous quote's result back on screen.
  const runIdRef = useRef(0)

  const previousInputs = usePrevious({
    analysis,
    isEnabled,
    useCacheValueForKey,
    workflowInputKey
  })

  const inputsChanged = haveResolveInputsChanged({
    previous: previousInputs,
    current: { analysis, isEnabled, useCacheValueForKey, workflowInputKey }
  })

  // An answer only counts while the inputs it came from are still the current
  // ones. Anything else is about a previous quote, and showing it as a settled
  // answer would be worse than showing nothing.
  const isCurrent =
    resolved !== undefined &&
    resolved.workflowInputKey === workflowInputKey &&
    resolved.useCacheValueForKey === useCacheValueForKey &&
    dequal(resolved.analysis, analysis)

  const resolution = isCurrent ? resolved?.value : undefined

  // React paints the render where the inputs changed before it runs the effect
  // that acts on them, so for one frame we owe a fetch we haven't started. We
  // count that frame as busy, otherwise the caller sees "finished, nothing to
  // report" and acts on it.
  const isResolvingDependencies =
    isRunning || (isEnabled && Boolean(analysis) && !resolution)

  const startDependencyResolutionRun = useCallback(
    async (inputs: ResolveInputs, refresh?: string[]) => {
      runIdRef.current += 1
      const runId = runIdRef.current

      setIsRunning(true)

      const result = await resolveDependencies({
        dependencies: inputs.analysis.dependencies,
        workflowInput: inputs.workflowInput,
        useCacheValueFor: inputs.useCacheValueFor,
        refresh
      })

      // A newer round started while we were waiting, and it owns what gets
      // shown now. Drop this one quietly rather than writing over it.
      if (runId !== runIdRef.current) return

      setResolved({
        analysis: inputs.analysis,
        workflowInputKey: inputs.workflowInputKey,
        useCacheValueForKey: inputs.useCacheValueForKey,
        value: result
      })
      setIsRunning(false)
    },
    []
  )

  useEffect(() => {
    if (!isEnabled || !analysis) {
      setIsRunning(false)
      return
    }

    // Anything else is a re-render for a reason that has nothing to do with
    // what we'd be fetching, so there's no new answer to go and get.
    if (!inputsChanged) return

    void startDependencyResolutionRun({
      analysis,
      workflowInput,
      useCacheValueFor,
      workflowInputKey,
      useCacheValueForKey
    })
  }, [
    analysis,
    inputsChanged,
    isEnabled,
    startDependencyResolutionRun,
    useCacheValueFor,
    useCacheValueForKey,
    workflowInput,
    workflowInputKey
  ])

  const refetchDependencies = useCallback(
    async (semanticTypes: string[]) => {
      if (!analysis) return

      const refresh = selectStaleDependencies({
        dependencies: analysis.dependencies,
        semanticTypes
      })
      if (refresh.length === 0) return

      await startDependencyResolutionRun(
        {
          analysis,
          workflowInput,
          useCacheValueFor,
          workflowInputKey,
          useCacheValueForKey
        },
        refresh
      )
    },
    [
      analysis,
      startDependencyResolutionRun,
      useCacheValueFor,
      useCacheValueForKey,
      workflowInput,
      workflowInputKey
    ]
  )

  return { isResolvingDependencies, refetchDependencies, resolution }
}
